import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Tooltip as ReTooltip, Legend,
} from 'recharts';
import { ArrowLeft, Home, Plus, X, AlertTriangle, Share2, Loader2, Image as ImageIcon, Search, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import TriangleShareModal from '@/components/TriangleShareModal';
import AbbrTip from '@/components/AbbrTip';
import AbbrText from '@/components/AbbrText';
import { useAuth } from '@/contexts/AuthContext';
import SiteDisclaimer from '@/components/SiteDisclaimer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Triangle Compare page — overlays 2–5 Dividend Triangle radars on a
// single chart so investors can answer "which of these is the better
// triangle?". The URL itself is the share artifact:
//   /triangle/compare?tickers=KO,PEP,JNJ
//
// Recharts supports multiple <Radar/> series on the same RadarChart by
// flattening the data into per-axis rows where each ticker is a key,
// e.g. [{ metric: 'Revenue', KO: 54.8, PEP: 41.2, JNJ: 70.4 }, ...].
// We compute that shape from the bulk /triangle/compare endpoint.

// Distinct, theme-safe palette. Order matters — first ticker added gets
// emerald, second violet, etc. All readable in both light and dark mode.
const SERIES_COLORS = [
  { stroke: '#10b981', fill: 'rgba(16, 185, 129, 0.18)', name: 'emerald' },
  { stroke: '#8b5cf6', fill: 'rgba(139, 92, 246, 0.18)', name: 'violet' },
  { stroke: '#f59e0b', fill: 'rgba(245, 158, 11, 0.18)', name: 'amber' },
  { stroke: '#0ea5e9', fill: 'rgba(14, 165, 233, 0.18)', name: 'sky' },
  { stroke: '#ec4899', fill: 'rgba(236, 72, 153, 0.18)', name: 'pink' },
];

const MAX_TICKERS = 4;
const MIN_TICKERS = 1;

// ---------------------------------------------------------------------------
// TickerTypeahead — small combo box used to add tickers to the Compare page.
// Mirrors the search pattern from CompareModal.js but tunes the suggestion
// preview to what matters for the Triangle: dividend yield + dividend CAGR
// (the actual leg the chart is going to plot). EPS CAGR isn't pre-computed
// on stock_cache so we don't show it here — that would force an N+1
// yfinance.income_stmt call per keystroke which would be punishingly slow.
// ---------------------------------------------------------------------------
function TickerTypeahead({ draft, setDraft, onAdd, onPick, placeholder }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hi, setHi] = useState(0); // highlighted suggestion index for ↑↓↩
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!draft.trim()) {
      setResults([]);
      return;
    }
    setBusy(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/stocks/search`,
          { params: { q: draft.trim(), limit: 8 } }
        );
        setResults(Array.isArray(r.data) ? r.data : []);
        setHi(0);
      } catch {
        setResults([]);
      } finally {
        setBusy(false);
      }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [draft]);

  useEffect(() => {
    function onDocClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const submitPick = () => {
    const picked = results[hi];
    if (picked && picked.ticker) {
      onPick(picked.ticker.toUpperCase());
      setOpen(false);
    } else {
      // Fall back to whatever the user typed — keeps the page usable for
      // tickers that aren't in the cached universe yet.
      onAdd();
      setOpen(false);
    }
  };

  return (
    <div
      ref={boxRef}
      className="relative"
      data-testid="triangle-compare-add-typeahead"
    >
      <div className="flex items-center gap-1">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={draft}
            onChange={(e) => { setDraft(e.target.value.toUpperCase()); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHi(h => Math.min(results.length - 1, h + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHi(h => Math.max(0, h - 1));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                submitPick();
              } else if (e.key === 'Escape') {
                setOpen(false);
              }
            }}
            placeholder={placeholder}
            className="pl-6 pr-2 py-1 text-xs rounded-md border border-border bg-background w-32 sm:w-40 uppercase outline-none focus:ring-2 focus:ring-primary/40"
            data-testid="triangle-compare-add-input"
            maxLength={10}
            autoComplete="off"
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={submitPick}
          disabled={!draft.trim() && results.length === 0}
          data-testid="triangle-compare-add-btn"
        >
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>

      {open && draft.trim().length > 0 && (
        <div
          className="absolute left-0 top-full z-50 mt-1 w-[280px] sm:w-[320px] max-h-72 overflow-y-auto rounded-lg border bg-popover shadow-lg"
          data-testid="triangle-compare-add-results"
        >
          {busy && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Searching…
            </div>
          )}
          {!busy && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-muted-foreground">
              No matches for &ldquo;{draft.trim()}&rdquo;. Press <kbd className="px-1 py-0.5 rounded bg-muted text-[10px]">↵</kbd> to add anyway.
            </div>
          )}
          {/* Column header line — explicitly labels the right-side columns
              so users know what the two numbers mean. Grid uses fixed
              widths for the two metric columns so missing data renders
              "—" in the same slot the number would occupy, keeping every
              row's columns aligned. */}
          {results.length > 0 && (
            <div className="grid grid-cols-[minmax(0,1fr)_64px_72px] items-center gap-3 px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.12em] text-muted-foreground bg-muted/40 border-b border-border/40">
              <span>Ticker / Name</span>
              <span className="text-right">Yield</span>
              <span className="text-right">5-yr Div CAGR</span>
            </div>
          )}
          {results.map((s, idx) => {
            const yld = Number(s.dividend_yield || 0);
            const dgr = s.cagr_5yr;
            const dgrTone =
              dgr === null || dgr === undefined ? 'text-muted-foreground'
              : dgr >= 5 ? 'text-emerald-600 dark:text-emerald-400'
              : dgr >= 0 ? 'text-amber-600 dark:text-amber-400'
              : 'text-rose-600 dark:text-rose-400';
            return (
              <button
                key={s.ticker}
                type="button"
                onMouseEnter={() => setHi(idx)}
                onClick={() => { onPick(s.ticker.toUpperCase()); setOpen(false); }}
                className={`grid grid-cols-[minmax(0,1fr)_64px_72px] items-center gap-3 w-full px-3 py-2 text-left border-b border-border/40 last:border-b-0 transition-colors ${idx === hi ? 'bg-muted' : 'hover:bg-muted/60'}`}
                data-testid={`triangle-compare-add-result-${s.ticker}`}
              >
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-bold font-mono">{s.ticker}</span>
                  <span className="block truncate text-[11px] text-muted-foreground" title={s.name}>{s.name}</span>
                </div>
                <span className={`text-right text-sm font-bold font-mono ${yld > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
                  {yld > 0 ? `${yld.toFixed(2)}%` : '—'}
                </span>
                <span className={`text-right text-sm font-bold font-mono ${dgrTone}`}>
                  {dgr === null || dgr === undefined
                    ? '—'
                    : `${dgr >= 0 ? '+' : ''}${dgr.toFixed(1)}%`}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


function fmtPct(n) {
  if (n === null || n === undefined) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${Number(n).toFixed(1)}%`;
}

function cagrTone(cagr) {
  if (cagr === null || cagr === undefined) return 'text-muted-foreground';
  if (cagr >= 8) return 'text-emerald-600 dark:text-emerald-400';
  if (cagr >= 3) return 'text-blue-600 dark:text-blue-400';
  if (cagr >= 0) return 'text-amber-600 dark:text-amber-400';
  return 'text-rose-600 dark:text-rose-400';
}

function scoreTone(score) {
  if (score === null || score === undefined) return 'text-muted-foreground';
  if (score >= 70) return 'text-emerald-600 dark:text-emerald-400';
  if (score >= 50) return 'text-blue-600 dark:text-blue-400';
  if (score >= 35) return 'text-amber-600 dark:text-amber-400';
  return 'text-rose-600 dark:text-rose-400';
}


export default function TriangleCompare() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTickers = useMemo(() => {
    const raw = searchParams.get('tickers') || '';
    return raw.split(',').map(t => t.trim().toUpperCase()).filter(Boolean).slice(0, MAX_TICKERS);
  }, [searchParams]);

  const [tickers, setTickers] = useState(initialTickers);
  const [draft, setDraft] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [isDark, setIsDark] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // See note in TriangleStandalone.js — `location.key !== 'default'` is the
  // reliable React-Router signal that the user navigated here from inside
  // the app (vs. landing on a deep / shared link cold).
  const [mode] = useState(() => (location.key && location.key !== 'default') ? 'back' : 'home');

  useEffect(() => {
    const compute = () => setIsDark(
      typeof document !== 'undefined'
      && document.documentElement.classList.contains('dark')
    );
    compute();
    const obs = new MutationObserver(compute);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  // Fetch payloads whenever the ticker list changes.
  useEffect(() => {
    if (tickers.length === 0) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    setErr(null);
    axios.get(`${API}/stocks/triangle/compare`, {
      params: { tickers: tickers.join(',') },
      withCredentials: true,
    })
      .then((r) => { if (!cancelled) setResults(r.data?.results || []); })
      .catch((e) => {
        if (!cancelled) setErr(e?.response?.data?.detail || 'Could not load comparison.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tickers]);

  // Keep the URL synced with the current ticker list so /triangle/compare
  // is always shareable as the canonical view of what's on screen.
  useEffect(() => {
    const next = tickers.join(',');
    const current = searchParams.get('tickers') || '';
    if (next !== current) {
      setSearchParams(next ? { tickers: next } : {}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickers]);

  const addTicker = useCallback(() => {
    const t = draft.trim().toUpperCase();
    if (!t) return;
    if (!/^[A-Z0-9.\-]+$/.test(t)) {
      toast.error('Ticker can only contain letters, numbers, dots, dashes.');
      return;
    }
    if (tickers.includes(t)) {
      toast.error(`${t} is already on the chart.`);
      setDraft('');
      return;
    }
    if (tickers.length >= MAX_TICKERS) {
      toast.error(`Max ${MAX_TICKERS} tickers per comparison.`);
      return;
    }
    setTickers([...tickers, t]);
    setDraft('');
  }, [draft, tickers]);

  const removeTicker = useCallback((t) => {
    setTickers(tickers.filter(x => x !== t));
  }, [tickers]);

  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const refId = user?.id || user?.user_id || user?.email;
    const refParam = refId ? `&ref=${encodeURIComponent(refId)}` : '';
    return `${window.location.origin}/triangle/compare?tickers=${tickers.join(',')}${refParam}`;
  }, [tickers, user]);

  const copyShare = useCallback(async () => {
    if (!tickers.length) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Compare link copied');
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = shareUrl;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success('Compare link copied');
      } catch {
        toast.error('Could not copy. URL: ' + shareUrl);
      }
    }
  }, [shareUrl, tickers]);

  // Only valid (no-error) results AND only tickers still in the chip list.
  // Filtering by `tickers.includes` is critical — when the user removes a
  // chip, `results` still contains stale data for that ticker for one
  // render until the next fetch settles. Without this filter the radar
  // would try to color a series whose ticker is no longer in `tickers`,
  // and `tickers.indexOf(r.ticker)` would return -1, causing
  // `SERIES_COLORS[-1].stroke` to throw.
  const validResults = useMemo(
    () => results.filter(r => !r.error && tickers.includes(r.ticker)),
    [results, tickers]
  );
  const errorResults = useMemo(
    () => results.filter(r => r.error && tickers.includes(r.ticker)),
    [results, tickers]
  );

  // Recharts data shape: one row per metric (Revenue / EPS / Dividend),
  // with each ticker as its own key for the radar value (0-100). Built
  // from `validResults` so stale tickers can never leak into the chart.
  const radarData = useMemo(() => {
    const rows = [
      { metric: 'Revenue', _axis: 'revenue' },
      { metric: 'EPS', _axis: 'eps' },
      { metric: 'Dividend', _axis: 'dividend' },
    ];
    for (const r of validResults) {
      for (const row of rows) {
        row[r.ticker] = r.radar?.[row._axis] ?? 0;
      }
    }
    return rows;
  }, [validResults]);

  // Defensive color lookup — always returns a valid palette entry even
  // if the ticker is somehow not in the current list (defence in depth).
  const colorFor = useCallback((ticker) => {
    const idx = tickers.indexOf(ticker);
    if (idx < 0) return SERIES_COLORS[0];
    return SERIES_COLORS[idx % SERIES_COLORS.length];
  }, [tickers]);

  // Keyboard shortcut: Esc goes back when the user navigated in-app.
  // Same rationale as TriangleStandalone — don't bounce cold-link visitors.
  // Also guard against firing while typing in the ticker typeahead or
  // while the share modal owns focus.
  useEffect(() => {
    if (mode !== 'back') return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (shareOpen) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      e.preventDefault();
      navigate(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, shareOpen, navigate]);

  return (
    <div className="min-h-screen min-h-[100svh] bg-background">
      <div className="mx-auto max-w-5xl px-4 pt-6 sm:pt-8 pb-0" data-testid="triangle-compare-page">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(mode === 'back' ? -1 : '/')}
            data-testid="triangle-compare-back-btn"
          >
            {mode === 'back' ? (
              <><ArrowLeft className="w-4 h-4 mr-1.5" /> Back</>
            ) : (
              <><Home className="w-4 h-4 mr-1.5" /> Home</>
            )}
          </Button>
          <div className="flex items-center gap-3">
            {mode === 'back' && (
              <span
                className="hidden sm:inline-flex items-center gap-1 text-[10px] text-muted-foreground/70"
                data-testid="triangle-compare-esc-hint"
              >
                Press <kbd className="px-1 py-0.5 rounded bg-muted text-[10px] font-mono">Esc</kbd> to go back
              </span>
            )}
            <Link
              to="/glossary#entry-dividend-triangle"
              target="_blank"
              rel="noopener"
              className="text-xs text-muted-foreground hover:text-foreground"
              data-testid="triangle-compare-glossary-link"
              title="Open the Dividend Triangle definition in the Glossary"
            >
              What is the Triangle?
            </Link>
          </div>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-1" data-testid="triangle-compare-title">
          Dividend Triangle · Compare
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          Overlay up to {MAX_TICKERS} dividend triangles on one chart. Same axes,
          same math, different companies — see at a glance which one has the
          healthiest growth profile.
        </p>

        {/* Ticker chip editor */}
        <div className="rounded-lg border bg-card p-3 mb-4">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {tickers.map((t, i) => {
              const c = SERIES_COLORS[i % SERIES_COLORS.length];
              return (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border"
                  style={{ borderColor: c.stroke, color: c.stroke, background: c.fill }}
                  data-testid={`triangle-compare-chip-${t}`}
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ background: c.stroke }}
                    aria-hidden
                  />
                  {t}
                  <button
                    type="button"
                    onClick={() => removeTicker(t)}
                    className="ml-1 hover:opacity-70"
                    aria-label={`Remove ${t}`}
                    data-testid={`triangle-compare-chip-remove-${t}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
            {tickers.length < MAX_TICKERS && (
              <TickerTypeahead
                draft={draft}
                setDraft={setDraft}
                onAdd={addTicker}
                onPick={(picked) => {
                  // Picking from the suggestion list skips the regex
                  // check (we trust /api/stocks/search) and adds directly.
                  if (tickers.includes(picked)) {
                    toast.error(`${picked} is already on the chart.`);
                    setDraft('');
                    return;
                  }
                  if (tickers.length >= MAX_TICKERS) {
                    toast.error(`Max ${MAX_TICKERS} tickers per comparison.`);
                    return;
                  }
                  setTickers([...tickers, picked]);
                  setDraft('');
                }}
                placeholder={tickers.length === 0 ? 'e.g. KO' : 'Add ticker'}
              />
            )}
          </div>
          <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
            <span>{tickers.length} of {MAX_TICKERS} tickers · n=adaptive 3-5 yr CAGR per leg</span>
            {tickers.length > 0 && (
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShareOpen(true)}
                  disabled={loading || results.filter(r => !r.error).length === 0}
                  data-testid="triangle-compare-share-card-btn"
                  className="h-7 px-2 text-[11px]"
                >
                  <ImageIcon className="w-3 h-3 mr-1" /> Share card
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyShare}
                  data-testid="triangle-compare-share-btn"
                  className="h-7 px-2 text-[11px]"
                >
                  <Share2 className="w-3 h-3 mr-1" /> Copy link
                </Button>
              </div>
            )}
          </div>
        </div>

        {err && (
          <div
            className="rounded-lg border border-rose-300/70 bg-rose-50/60 dark:bg-rose-950/30 dark:border-rose-800/50 p-3 mb-4 text-xs text-rose-800 dark:text-rose-200"
            data-testid="triangle-compare-error"
          >
            {err}
          </div>
        )}

        {tickers.length === 0 && !loading && (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center" data-testid="triangle-compare-empty">
            <p className="text-sm text-muted-foreground mb-2">
              Add 2 or more tickers above to overlay their triangles.
            </p>
            <p className="text-[11px] text-muted-foreground">
              Try <button className="underline" onClick={() => setTickers(['KO', 'PEP'])}>KO vs PEP</button>{' '}
              or <button className="underline" onClick={() => setTickers(['MSFT', 'AAPL', 'GOOGL'])}>MSFT vs AAPL vs GOOGL</button>.
            </p>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12" data-testid="triangle-compare-loading">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading triangles…</span>
          </div>
        )}

        {!loading && validResults.length > 0 && (
          <>
            {/* REIT-aware ribbon. Renders when ≥1 ticker in the
                comparison is a REIT — gives users the right mental
                model for interpreting the EPS leg before they look at
                the radar. Click-through links to /glossary so they can
                read the full "Why EPS Lies for REITs" entry. */}
            {validResults.some(r => r.is_reit) && (
              <div
                className="mb-4 rounded-lg border border-violet-300 dark:border-violet-800 bg-violet-50/70 dark:bg-violet-950/30 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1"
                data-testid="triangle-compare-reit-ribbon"
              >
                <Building2 className="w-4 h-4 text-violet-600 dark:text-violet-300 shrink-0" />
                <p className="text-xs leading-snug text-violet-900 dark:text-violet-100 flex-1 min-w-[200px]">
                  <span className="font-bold">
                    {validResults.filter(r => r.is_reit).length === validResults.length
                      ? 'All comparison tickers are REITs.'
                      : `${validResults.filter(r => r.is_reit).length} of ${validResults.length} tickers in this comparison are REITs.`}
                  </span>
                  {' '}The <AbbrTip term="EPS">EPS</AbbrTip> leg can drag a
                  healthy REIT&rsquo;s score down because depreciation is
                  non-cash — open each REIT&rsquo;s page and check the{' '}
                  <span className="font-bold">REIT Cash Lens</span> for
                  the <AbbrTip term="FFO">FFO</AbbrTip>-based payout
                  ratio that income investors actually use.
                </p>
                <Link
                  to="/glossary#letter-W"
                  target="_blank"
                  rel="noopener"
                  className="text-xs font-bold text-violet-700 dark:text-violet-200 hover:text-violet-900 dark:hover:text-white shrink-0 underline-offset-2 hover:underline"
                  data-testid="triangle-compare-reit-ribbon-glossary"
                  title="Open the Glossary in a new tab"
                >
                  Why EPS lies for REITs ↗
                </Link>
              </div>
            )}

            {/* Overlay radar chart */}
            <div
              className="rounded-lg border bg-card p-3 mb-4"
              style={{ height: 380 }}
              data-testid="triangle-compare-radar"
            >
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} outerRadius="70%">
                  <PolarGrid stroke={isDark ? '#374151' : '#e5e7eb'} />
                  <PolarAngleAxis
                    dataKey="metric"
                    tick={{ fill: isDark ? '#d1d5db' : '#374151', fontSize: 12, fontWeight: 600 }}
                  />
                  <PolarRadiusAxis
                    angle={90}
                    domain={[0, 100]}
                    tick={{ fill: isDark ? '#6b7280' : '#9ca3af', fontSize: 10 }}
                    tickCount={5}
                  />
                  {validResults.map((r) => {
                    const c = colorFor(r.ticker);
                    return (
                      <Radar
                        key={r.ticker}
                        name={r.ticker}
                        dataKey={r.ticker}
                        stroke={c.stroke}
                        fill={c.fill}
                        strokeWidth={2}
                        isAnimationActive
                      />
                    );
                  })}
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    iconType="circle"
                  />
                  <ReTooltip
                    contentStyle={{
                      backgroundColor: isDark ? '#1f2937' : '#fff',
                      border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Comparison table — CAGR per leg + composite score + trap flag */}
            <div className="rounded-lg border bg-card overflow-hidden" data-testid="triangle-compare-table">
              <div className="grid grid-cols-12 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground bg-muted/30 border-b border-border">
                <div className="col-span-3 sm:col-span-2">Ticker</div>
                <div className="col-span-2 text-right">Revenue</div>
                <div className="col-span-2 text-right">EPS</div>
                <div className="col-span-2 text-right">Dividend</div>
                <div className="col-span-2 text-right">Score</div>
                <div className="col-span-1 sm:col-span-2 text-right">Flag</div>
              </div>
              {/* Sort table rows best-score-first so the strongest
                  triangle is always at the top. Chips + radar stay in
                  user-entry order so the colors users see in the chip
                  bar match the dots on each table row. Missing scores
                  sink to the bottom. */}
              {[...validResults]
                .sort((a, b) => (b?.triangle_score ?? -1) - (a?.triangle_score ?? -1))
                .map((r) => {
                const c = colorFor(r.ticker);
                return (
                  <div
                    key={r.ticker}
                    className="grid grid-cols-12 px-3 py-2.5 text-sm border-b border-border last:border-b-0 hover:bg-muted/20 transition-colors"
                    data-testid={`triangle-compare-row-${r.ticker}`}
                  >
                    <div className="col-span-3 sm:col-span-2 flex items-center gap-2 font-semibold">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ background: c.stroke }}
                        aria-hidden
                      />
                      <Link
                        to={`/triangle/${r.ticker}`}
                        className="hover:underline truncate"
                      >
                        {r.ticker}
                      </Link>
                      {r.is_reit && (
                        // Small REIT marker so users instantly see which
                        // rows are being penalised by the EPS leg (heavy
                        // depreciation drags GAAP EPS even on healthy
                        // REITs). The aggregate ribbon above the table
                        // tells them what to do; this badge tells them
                        // which row to apply that context to.
                        <AbbrTip term="REIT">
                          <span
                            className="inline-flex items-center gap-0.5 px-1 py-px rounded text-[9px] font-bold bg-violet-100 text-violet-700 border border-violet-300 dark:bg-violet-950/60 dark:text-violet-200 dark:border-violet-800"
                            data-testid={`triangle-compare-reit-${r.ticker}`}
                          >
                            <Building2 className="w-2.5 h-2.5" />
                            REIT
                          </span>
                        </AbbrTip>
                      )}
                      {/* DiviDrip's Opinion pill — compact verdict label
                          per ticker so users can scan the comparison
                          without clicking into each Triangle page. Full
                          reasoning lives in the standalone
                          /triangle/[ticker] page via DividripOpinionBanner. */}
                      {r.opinion?.verdict && (
                        <span
                          title={`DiviDrip's Opinion: ${r.opinion.headline || r.opinion.verdict}`}
                          className={`inline-flex items-center px-1.5 py-px rounded text-[9px] font-bold uppercase tracking-[0.05em] border ${
                            r.opinion.verdict === 'buy'
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-200 dark:border-emerald-800'
                              : r.opinion.verdict === 'avoid'
                              ? 'bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950/60 dark:text-rose-200 dark:border-rose-800'
                              : 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600'
                          }`}
                          data-testid={`triangle-compare-opinion-${r.ticker}`}
                        >
                          {r.opinion.verdict === 'buy' ? 'Buy' : r.opinion.verdict === 'avoid' ? 'Not Buy' : 'Neutral'}
                        </span>
                      )}
                    </div>
                    <div className={`col-span-2 text-right font-mono ${cagrTone(r.revenue?.cagr_pct)}`}>
                      {fmtPct(r.revenue?.cagr_pct)}
                    </div>
                    <div className={`col-span-2 text-right font-mono ${cagrTone(r.eps?.cagr_pct)}`}>
                      {fmtPct(r.eps?.cagr_pct)}
                    </div>
                    <div className={`col-span-2 text-right font-mono ${cagrTone(r.dividend?.cagr_pct)}`}>
                      {fmtPct(r.dividend?.cagr_pct)}
                    </div>
                    <div className={`col-span-2 text-right font-mono font-bold ${scoreTone(r.triangle_score)}`}>
                      {r.triangle_score ?? '—'}
                    </div>
                    <div className="col-span-1 sm:col-span-2 text-right">
                      {r.trap_flag ? (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-100 dark:bg-rose-950/60 text-rose-800 dark:text-rose-300"
                          title={r.trap_reason || 'Potential dividend trap (dividend growing faster than earnings or revenue). Always pair with the Quality Score before buying.'}
                          data-testid={`triangle-compare-trap-${r.ticker}`}
                        >
                          <AlertTriangle className="w-3 h-3" />
                          <span className="hidden sm:inline">Trap</span>
                        </span>
                      ) : r.triangle_score !== null && r.triangle_score !== undefined && r.triangle_score < 35 ? (
                        // Even when the formal trap-detector didn't fire,
                        // a sub-35 score lands in the "Trap / Weak" band on
                        // the BadgeKey — surface that explicitly here so
                        // the Flag column always tells the user something
                        // useful for ETFs / single-leg tickers where the
                        // trap signature can't be computed.
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-100 dark:bg-rose-950/60 text-rose-800 dark:text-rose-300"
                          title="Weak Triangle Score. The composite is low because most of the growth legs are flat, negative, or unavailable. Always pair with the Quality Score before buying."
                          data-testid={`triangle-compare-weak-${r.ticker}`}
                        >
                          <AlertTriangle className="w-3 h-3" />
                          <span className="hidden sm:inline">Weak</span>
                        </span>
                      ) : r.legs_available !== undefined && r.legs_available !== null && r.legs_available < 3 ? (
                        // Partial-data score (1-2 of 3 legs available).
                        // No trap signature, score isn't weak — just signal
                        // that the comparison is apples-to-oranges for
                        // this row.
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-100 dark:bg-sky-950/60 text-sky-800 dark:text-sky-300"
                          title={`Approximate — only ${r.legs_available} of 3 legs available for this ticker. Common for ETFs and newer companies.`}
                          data-testid={`triangle-compare-partial-${r.ticker}`}
                        >
                          ~
                          <span className="hidden sm:inline">Partial</span>
                        </span>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">—</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Per-ticker trap reasons — only show when at least one fires. */}
            {validResults.some(r => r.trap_flag) && (
              <div className="mt-3 space-y-1.5" data-testid="triangle-compare-trap-reasons">
                {validResults.filter(r => r.trap_flag).map((r) => (
                  <div
                    key={r.ticker}
                    className="flex items-start gap-2 rounded-lg border border-rose-300/70 bg-rose-50/60 dark:bg-rose-950/30 dark:border-rose-800/50 p-2.5 text-xs text-rose-800 dark:text-rose-200"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold">{r.ticker}:</span>{' '}
                      <AbbrText text={r.trap_reason} as="span" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {errorResults.length > 0 && (
          <div className="mt-3 text-[11px] text-muted-foreground" data-testid="triangle-compare-bad-tickers">
            Couldn't find triangle data for: {errorResults.map(r => r.ticker).join(', ')}.
          </div>
        )}

        <div
          className="mt-8 max-w-prose mx-auto rounded-lg border-2 border-red-400 dark:border-red-700 px-5 py-4"
          data-testid="triangle-compare-disclaimer"
        >
          <p className="text-xs font-medium leading-relaxed text-amber-900 dark:text-yellow-400 text-left">
            Dividend Triangle Compare is an educational visualization, not investment
            advice. Always do your own research before buying or selling.
          </p>
        </div>
      </div>
      <SiteDisclaimer />

      <TriangleShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        results={validResults}
        mode="compare"
      />
    </div>
  );
}
